import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  AddVisitorAppointmentBody,
  ApproveAppointmentBody,
  CheckDuplicateAppointmentBody,
  GetSearchedUserDetailsBody,
  RejectAppointmentBody,
  ApproveRejectDeviceBody,
  UpdateVisitorAppointmentBody,
  GetDevicesByIdBody,
  GetFileByTypeBody,
  RequestMoreInfoBody,
  IsAlreadyCheckedInBody,
  CheckInVisitorBody,
  CheckOutVisitorBody,
  PrintBadgeForVisitorBody,
  GetBadgePrintInfoByIdBody,
  GetBulkBadgePrintInfoByIdBody,
  BulkUpdateBadgePrintStatusBody,
  ValidatePhotoBody,
  CancelAppointmentByIdBody,
  ExportApprovedVisitRequestsForExcelPDFBody,
  AddBlacklistBody,
  RemoveFromBlacklistingBody,
  GetDeviceDetailsByIdBody,
  RemoveAppointmentAttachmentBody,
  ForceCheckOutVisitorBody,
} from "../interface";
import { FETCH_DASHBOARD_DETAILS_KEYS } from "../../dashboard/query";
import { FETCH_VISITOR_DETAILS_KEYS } from "../query";

const getDeviceDetailsByIdAction = async (body: GetDeviceDetailsByIdBody) => {
  return Post({
    url: "/api/Appointment/GetDeviceDetailsById",
    body,
  });
};

export const useGetDeviceDetailsById = () => {
  return useMutation({
    mutationFn: async (body: GetDeviceDetailsByIdBody) =>
      getDeviceDetailsByIdAction(body),
  });
};
//

const removeFromBlacklistingAction = async (
  body: RemoveFromBlacklistingBody
) => {
  return Post({
    url: "/api/Blacklist/Delete",
    body,
  });
};

export const useRemoveFromBlacklisting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: RemoveFromBlacklistingBody) =>
      removeFromBlacklistingAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          FETCH_VISITOR_DETAILS_KEYS.FETCH_GET_ALL_BLACKLISTED_VISITORS,
        ],
      });
    },
  });
};

const addBlacklistAction = async (body: AddBlacklistBody) => {
  return Post({
    url: "/api/Blacklist/Add",
    body,
  });
};

export const useAddBlacklist = () => {
  return useMutation({
    mutationFn: async (body: AddBlacklistBody) => addBlacklistAction(body),
  });
};

const cancelAppointmentByIdAction = async (body: CancelAppointmentByIdBody) => {
  return Post({
    url: "/api/Appointment/Cancel",
    body,
  });
};

export const useCancelAppointmentById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelAppointmentByIdBody) =>
      cancelAppointmentByIdAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [
          FETCH_VISITOR_DETAILS_KEYS.FETCH_ALL_SEARCH_VISITOR_APPOINTMENTS,
        ],
      }),
  });
};

const getBadgePrintInfoByIdAction = async (body: GetBadgePrintInfoByIdBody) => {
  return Post({
    url: "/api/Appointment/GetBadgePrintInfoById",
    body,
  });
};

export const useGetBadgePrintInfoById = () => {
  return useMutation({
    mutationFn: async (body: GetBadgePrintInfoByIdBody) =>
      getBadgePrintInfoByIdAction(body),
  });
};

const getBulkBadgePrintInfoByIdAction = async (
  body: GetBulkBadgePrintInfoByIdBody[]
) => {
  return Post({
    url: "/api/Appointment/GetBulkBadgePrintInfoByIds",
    body,
  });
};

export const useGetBulkBadgePrintInfoById = () => {
  return useMutation({
    mutationFn: async (body: GetBulkBadgePrintInfoByIdBody[]) =>
      getBulkBadgePrintInfoByIdAction(body),
  });
};

const printBadgeForVisitorAction = async (body: PrintBadgeForVisitorBody) => {
  return Post({
    url: "/api/Appointment/UpdateBadgePrintStatus",
    body,
  });
};

export const usePrintBadgeForVisitor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: PrintBadgeForVisitorBody) =>
      printBadgeForVisitorAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [
          FETCH_DASHBOARD_DETAILS_KEYS.FETCH_PENDING_APPROVED_VISITORS,
        ],
      }),
  });
};

const bulkUpdateBadgePrintStatusAction = async (
  body: BulkUpdateBadgePrintStatusBody[]
) => {
  return Post({
    url: "/api/Appointment/BulkUpdateBadgePrintStatus",
    body,
  });
};

export const useBulkUpdateBadgePrintStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: BulkUpdateBadgePrintStatusBody[]) =>
      bulkUpdateBadgePrintStatusAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [
          FETCH_DASHBOARD_DETAILS_KEYS.FETCH_PENDING_APPROVED_VISITORS,
        ],
      }),
  });
};

const forceCheckOutVisitorAction = async (body: ForceCheckOutVisitorBody) => {
  return Post({
    url: "/api/Appointment/DeleteCheckinByCheckinID",
    body,
  });
};

export const useForceCheckOutVisitor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: ForceCheckOutVisitorBody) =>
      forceCheckOutVisitorAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_CHECKED_IN_VISITORS],
      });

      queryClient.invalidateQueries({
        queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_VISITORS_COUNT],
      });
    },
  });
};

const checkOutVisitorAction = async (body: CheckOutVisitorBody) => {
  return Post({
    url: "/api/Appointment/CheckedOut",
    body,
  });
};

export const useCheckOutVisitor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CheckOutVisitorBody) =>
      checkOutVisitorAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          FETCH_DASHBOARD_DETAILS_KEYS.FETCH_PENDING_APPROVED_VISITORS,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_CHECKED_IN_VISITORS],
      });

      queryClient.invalidateQueries({
        queryKey: [FETCH_DASHBOARD_DETAILS_KEYS.FETCH_VISITORS_COUNT],
      });
    },
  });
};

const checkInVisitorAction = async (body: CheckInVisitorBody) => {
  return Post({
    url: "/api/Appointment/CheckedIn",
    body,
  });
};

export const useCheckInVisitor = () => {
  return useMutation({
    mutationFn: async (body: CheckInVisitorBody) => checkInVisitorAction(body),
  });
};

const isAlreadyCheckedInAction = async (body: IsAlreadyCheckedInBody) => {
  return Post({
    url: "/api/Appointment/IsAlreadyCheckedIn",
    body,
  });
};

export const useIsAlreadyCheckedIn = () => {
  return useMutation({
    mutationFn: async (body: IsAlreadyCheckedInBody) =>
      isAlreadyCheckedInAction(body),
  });
};

const addVisitorAppointmentAction = async (body: AddVisitorAppointmentBody) => {
  return Post({
    url: "/api/Appointment/Add",
    body,
  });
};

export const useAddVisitorAppointment = () => {
  return useMutation({
    mutationFn: async (body: AddVisitorAppointmentBody) =>
      addVisitorAppointmentAction(body),
  });
};

const approveAppointmentAction = async (body: ApproveAppointmentBody) => {
  return Post({
    url: "/api/Appointment/UpdateAppointmentApprovalStatus",
    body,
  });
};

export const useApproveAppointment = () => {
  return useMutation({
    mutationFn: async (body: ApproveAppointmentBody) =>
      approveAppointmentAction(body),
  });
};

const rejectAppointmentAction = async (body: RejectAppointmentBody) => {
  return Post({
    url: "/api/Appointment/Reject",
    body,
  });
};

export const useRejectAppointment = () => {
  return useMutation({
    mutationFn: async (body: RejectAppointmentBody) =>
      rejectAppointmentAction(body),
  });
};

const addAttachmentAction = async (body: FormData) => {
  return Post({
    url: "/api/Appointment/AddAttachments",
    body,
    isFormData: true,
  });
};

export const useAddAttachment = () => {
  return useMutation({
    mutationFn: async (body: FormData) => addAttachmentAction(body),
  });
};

const checkDuplicateAppointmentAction = async (
  body: CheckDuplicateAppointmentBody
) => {
  return Post({
    url: "/api/Validation/DuplicateAppointment",
    body,
  });
};

export const useCheckDuplicateAppointment = () => {
  return useMutation({
    mutationFn: async (body: CheckDuplicateAppointmentBody) =>
      checkDuplicateAppointmentAction(body),
  });
};

const updateVisitorAppointmentAction = async (
  body: UpdateVisitorAppointmentBody
) => {
  return Post({
    url: "/api/Appointment/Update",
    body,
  });
};

export const useUpdateVisitorAppointment = () => {
  return useMutation({
    mutationFn: async (body: UpdateVisitorAppointmentBody) =>
      updateVisitorAppointmentAction(body),
  });
};

const getSearchedUserDetailsAction = async (
  body: GetSearchedUserDetailsBody
) => {
  return Post({
    url: "/api/Appointment/SearchUser",
    body,
  });
};

export const useGetSearchedUserDetails = () => {
  return useMutation({
    mutationFn: async (body: GetSearchedUserDetailsBody) =>
      getSearchedUserDetailsAction(body),
  });
};

const rejectDeviceAction = async (body: ApproveRejectDeviceBody) => {
  return Post({
    url: "/api/Appointment/BulkRejectDevices",
    body,
  });
};

export const useRejectDevice = () => {
  return useMutation({
    mutationFn: async (body: ApproveRejectDeviceBody) =>
      rejectDeviceAction(body),
  });
};

const approveDeviceAction = async (body: ApproveRejectDeviceBody) => {
  return Post({
    url: "/api/Appointment/BulkApproveDevices",
    body,
  });
};

export const useApproveDevice = () => {
  return useMutation({
    mutationFn: async (body: ApproveRejectDeviceBody) =>
      approveDeviceAction(body),
  });
};

const getDevicesByIdAction = async (body: GetDevicesByIdBody) => {
  return Post({
    url: "/api/Appointment/GetDevices",
    body,
  });
};

export const useGetDevicesById = () => {
  return useMutation({
    mutationFn: async (body: GetDevicesByIdBody) => getDevicesByIdAction(body),
  });
};

const getFileByTypeAction = async (body: GetFileByTypeBody) => {
  return Post({
    url: "/api/Appointment/GetFile",
    body,
  });
};

export const useGetFileByType = () => {
  return useMutation({
    mutationFn: async (body: GetFileByTypeBody) => getFileByTypeAction(body),
  });
};

const requestMoreInfoFromStaffAction = async (body: RequestMoreInfoBody) => {
  return Post({
    url: "/api/Appointment/RequestMoreInfo",
    body,
  });
};

export const useRequestMoreInfoFromStaff = () => {
  return useMutation({
    mutationFn: async (body: RequestMoreInfoBody) =>
      requestMoreInfoFromStaffAction(body),
  });
};

const validatePhotoAction = async (body: ValidatePhotoBody) => {
  return Post({
    url: "/api/FR/ValidatePhoto",
    body,
  });
};

export const useValidatePhoto = () => {
  return useMutation({
    mutationFn: async (body: ValidatePhotoBody) => validatePhotoAction(body),
  });
};

const ExportSearchVisitRequestsForExcelAction = async (
  body: ExportApprovedVisitRequestsForExcelPDFBody
) => {
  return Post({
    url: "/api/Export/Excel/SearchAppointments",
    body,
    isReturnBlob: true,
  });
};

export const useExportSearchVisitRequestsForExcel = () => {
  return useMutation({
    mutationFn: async (body: ExportApprovedVisitRequestsForExcelPDFBody) =>
      ExportSearchVisitRequestsForExcelAction(body),
  });
};

const ExportSearchVisitRequestsForPDFAction = async (
  body: ExportApprovedVisitRequestsForExcelPDFBody
) => {
  return Post({
    url: "/api/Export/PDF/SearchAppointments",
    body,
    isReturnBlob: true,
  });
};

export const useExportSearchVisitRequestsForPDF = () => {
  return useMutation({
    mutationFn: async (body: ExportApprovedVisitRequestsForExcelPDFBody) =>
      ExportSearchVisitRequestsForPDFAction(body),
  });
};

const ExportApproveVisitRequestsForExcelAction = async (
  body: ExportApprovedVisitRequestsForExcelPDFBody
) => {
  return Post({
    url: "/api/Export/Excel/ApproveAppointments",
    body,
    isReturnBlob: true,
  });
};

export const useExportApproveVisitRequestsForExcel = () => {
  return useMutation({
    mutationFn: async (body: ExportApprovedVisitRequestsForExcelPDFBody) =>
      ExportApproveVisitRequestsForExcelAction(body),
  });
};

const removeAppointmentAttachmentAction = async (
  body: RemoveAppointmentAttachmentBody
) => {
  return Post({
    url: "/api/Appointment/RemoveAttachment",
    body,
  });
};

export const useRemoveAppointmentAttachment = () => {
  return useMutation({
    mutationFn: async (body: RemoveAppointmentAttachmentBody) =>
      removeAppointmentAttachmentAction(body),
  });
};

const ExportApproveVisitRequestsForPDFAction = async (
  body: ExportApprovedVisitRequestsForExcelPDFBody
) => {
  return Post({
    url: "/api/Export/PDF/ApproveAppointments",
    body,
    isReturnBlob: true,
  });
};

export const useExportApproveVisitRequestsForPDF = () => {
  return useMutation({
    mutationFn: async (body: ExportApprovedVisitRequestsForExcelPDFBody) =>
      ExportApproveVisitRequestsForPDFAction(body),
  });
};
